/**
 * SamSessionScreen — every off-live activity tap from the todo lands
 * here. Sam wraps the standard activity flow:
 *
 *  - Pas d'en-tête propre : l'en-tête de l'activité (retour, titre, matière,
 *                 chrono) est le seul, et il dit déjà tout. Le nôtre répétait
 *                 la matière et empilait une deuxième bande avant l'énoncé —
 *                 la présence de Sam est portée par sa tuile flottante.
 *  - Center     : the regular `ActivityWrapperScreen` rendered inline,
 *                 same as the standard student play flow (quiz gate +
 *                 photo submission + scoring). currentActivity is
 *                 dispatched to Redux at mount so the wrapper picks it
 *                 up; the wrapper clears it on its own unmount.
 *  - Overlay    : Sam floating tile + conversation sheet
 *  - Fin        : la même que partout ailleurs. La copie rendue, l'écran
 *                 d'activité pose l'attente (avec son défi) puis la
 *                 correction ; Sam se range pendant ce temps, et son écran
 *                 de fin vient après, quand le wrapper le lui passe
 *                 (`onCompletionScreen`).
 *
 * Sam never auto-opens (D17/D21 reverted): the student opens the
 * conversation by tapping the floating tile.
 */
import { useIsFocusedM } from "@common/hooks/useIsFocusedM"
import { LoadingScreen } from "@common/screens/LoadingScreen"
import { theme } from "@common/theme/theme"
import { useCamera } from "@common/hooks/useCamera"
import { formIds } from "@data/constants/formConstants"
import { useSamContext } from "../../engine/activity/sam/useSamContext"
import { useSamConversation } from "../../engine/activity/sam/useSamConversation"
import { useSamProactivity } from "../../engine/activity/sam/useSamProactivity"
import { useSamWorkCheck } from "../../engine/activity/sam/useSamWorkCheck"
import MissionCompletionView from "./components/MissionCompletionView"
import SamConversationSheet from "./components/SamConversationSheet"
import SamFloatingTile, { SAM_TILE_CLEARANCE, SamTileClearance } from "./components/SamFloatingTile"
import ActivityWrapperScreen from "../activity/execute/ActivityWrapperScreen"
import useLaunchActivity from "@common/hooks/useLaunchActivity"
import { getMostRecentActivity } from "@data/utility/dataUtils"
import { setCurrentActivity as setCurrentActivityAction } from "@data/redux/actions/activity"
import { useNavigation, useRoute } from "@react-navigation/native"
import * as amplitude from "@amplitude/analytics-react-native"
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react"
import {
    AppState,
    StyleSheet,
    TouchableOpacity,
    View,
} from "react-native"
import { useDispatch, useSelector } from "react-redux"
import Animated, { FadeIn, FadeOut } from "react-native-reanimated"

// Délai laissé à la liste todo pour arriver dans le store avant de conclure
// que l'activité n'y est pas. Assez long pour couvrir un chargement lent,
// assez court pour ne pas laisser l'élève devant une roue qui tourne.
const MISSING_TODO_GRACE_MS = 6000

const SamSessionScreen = () => {
    const navigation = useNavigation()
    const route = useRoute()
    const dispatch = useDispatch()
    // La navigation interne passe l'id en NOMBRE (`navigation.push`), mais une
    // entrée par URL — deep-link, rechargement de page, lien partagé — le livre
    // en CHAÎNE. Toutes les comparaisons ci-dessous sont strictes : sans cette
    // normalisation, `2 === "2"` est faux, l'écran ne retrouve jamais son
    // activité dans la todo en cache et tourne indéfiniment.
    const activityId = Number(route.params?.activityId) || null

    // « L'élève est-il devant l'écran ? » Deux horloges tournent en
    // parallèle dans cette session — les relances proactives (5 s et 3 min)
    // et la bulle d'aperçu (7 s). Aucune ne savait que l'élève avait changé
    // d'onglet, verrouillé son téléphone ou quitté l'écran : il revenait sur
    // une conversation où Sam lui avait parlé tout seul. Un seul signal,
    // partagé par les deux.
    const isFocused = useIsFocusedM()
    const [isForeground, setIsForeground] = useState(
        AppState.currentState !== "background"
    )
    useEffect(() => {
        // Sur web, AppState "active"/"background" est câblé sur
        // `visibilitychange` par react-native-web : le changement d'onglet
        // est donc couvert par le même code que la mise en veille du mobile.
        const sub = AppState.addEventListener("change", (next) =>
            setIsForeground(next === "active")
        )
        return () => sub?.remove?.()
    }, [])
    const isStudentPresent = isFocused && isForeground

    const { context, isLoading, hasError } = useSamContext(activityId)
    const launchActivity = useLaunchActivity(navigation, route)
    const conversation = useSamConversation(activityId)

    // Pull the full Eloquent activity (status, material, prerequisite_quiz,
    // attachments...) from the cached todo list — sam-context's slimmed
    // payload isn't enough for the wrapper.
    const todoActivities = useSelector(
        (state) => state.data.currentUser?.object?.todo_activities
    )
    // On RETIENT l'activité une fois trouvée. À la validation, le reducer la
    // retire de `todo_activities` (`finalizeActivity._SUCCESS` la filtre), et
    // parfois la réponse n'embarque même pas la todo — la clé devient alors
    // `undefined`. Sans cette mémoire, l'activité disparaissait sous la garde
    // de rendu juste en dessous : l'élève voyait une roue à la place de sa
    // correction, puis le filet des 6 s le renvoyait à sa liste.
    const todoActivityFound = useMemo(
        () => todoActivities?.find((a) => a?.id === activityId),
        [todoActivities, activityId]
    )
    const todoActivityRef = useRef(null)
    useEffect(() => {
        if (todoActivityFound) todoActivityRef.current = todoActivityFound
    }, [todoActivityFound])
    const todoActivity = todoActivityFound || todoActivityRef.current
    // `undefined` avant le chargement, tableau après : c'est ce qui distingue
    // « la liste n'est pas encore là » de « la liste est là, sans cette
    // activité ». Le filet de sécurité plus bas dépend de cette nuance.
    const todoLoaded = Array.isArray(todoActivities)

    // « Activité suivante avec Sam ». La spec est explicite : ne PAS se
    // limiter à la séquence prescrite, et pas de cul-de-sac. Une activité de
    // programme de révisions ou libre doit enchaîner elle aussi. On honore
    // d'abord la suite de la séquence quand elle existe — c'est l'intention
    // du tuteur — puis on retombe sur la même sélection que le flux standard
    // (`getMostRecentActivity`, cf. useNextTodoActivity).
    const nextActivityId = useMemo(() => {
        const fromSequence = context?.sequence?.next_activity_id
        if (fromSequence) return fromSequence
        const remaining = (todoActivities || []).filter(
            (a) =>
                a?.id !== activityId &&
                !a?.isDeleted &&
                // La todo en cache n'est pas rafraîchie à chaque validation :
                // une activité terminée plus tôt dans la session y traîne
                // encore, avec son `finished_at`. Sans ce filtre,
                // getMostRecentActivity finit par la ressortir — ses deux
                // derniers replis ne regardent plus `finished_at` — et le CTA
                // renvoyait l'élève sur une activité déjà faite.
                !a?.finished_at
        )
        return getMostRecentActivity(remaining, null)?.id ?? null
    }, [context?.sequence?.next_activity_id, todoActivities, activityId])

    const wrapperActivityId = useSelector(
        (state) =>
            state.data?.currentActivity?.activity?.activityData?.id ?? null
    )
    const wrapperProgressStatus = useSelector(
        (state) =>
            state.data?.currentActivity?.activity?.progressStatus ?? null
    )
    const wrapperReady = wrapperActivityId === activityId

    const [sheetOpen, setSheetOpen] = useState(false)
    // Copie rendue : Sam se tait et se range, l'écran d'activité déroule la
    // fin (attente, défi, correction).
    const [submitted, setSubmitted] = useState(false)
    // L'écran de fin, tel que le wrapper le passe une fois cette fin jouée.
    const [completion, setCompletion] = useState(null)
    const [peekText, setPeekText] = useState(null)
    const startedAtRef = useRef(Date.now())
    const trackedStartRef = useRef(false)
    const trackedCompletionRef = useRef(false)
    const peekTimerRef = useRef(null)
    const degradedRef = useRef(false)
    const wrapperWasReadyRef = useRef(false)

    const closeSheet = useCallback(() => setSheetOpen(false), [])
    const openSheet = useCallback(() => setSheetOpen(true), [])
    // La photo partie, la conversation s'ouvre : la réponse de Sam y arrive.
    // L'appareil photo du quali, sans le scanner (`useCamera` vivait dans le crochet).
    const [launchWorkCheckCamera] = useCamera(formIds.SAM_WORK_CHECK, false, false)
    const workCheck = useSamWorkCheck(activityId, openSheet, launchWorkCheckCamera)

    // Remise à zéro quand on change d'activité SANS changer d'écran.
    //
    // « Activité suivante avec Sam » fait un `navigation.replace` sur la même
    // route : React Navigation réutilise l'instance, donc l'état survivait au
    // changement de `activityId`. L'écran de fin restant posé, il rendait
    // aussitôt l'écran de fin de l'activité PRÉCÉDENTE — vu de l'élève, le
    // bouton ne faisait rien. Les refs doivent suivre : sinon la session
    // suivante est comptée comme déjà démarrée, déjà terminée, déjà dégradée.
    const previousActivityIdRef = useRef(activityId)
    useEffect(() => {
        if (previousActivityIdRef.current === activityId) return
        previousActivityIdRef.current = activityId

        setSheetOpen(false)
        setSubmitted(false)
        setCompletion(null)
        setPeekText(null)

        startedAtRef.current = Date.now()
        trackedStartRef.current = false
        trackedCompletionRef.current = false
        degradedRef.current = false
        wrapperWasReadyRef.current = false
        todoActivityRef.current = null
        if (peekTimerRef.current) {
            clearTimeout(peekTimerRef.current)
            peekTimerRef.current = null
        }
    }, [activityId])

    // Surface the latest Sam message as a peek bubble next to the
    // floating tile while the sheet is closed — otherwise D-2 proactive
    // teasers (and any reactive Sam reply) stay invisible. Auto-clears
    // after 7 s or as soon as the student opens the sheet.
    const lastMessage =
        conversation.messages[conversation.messages.length - 1] || null
    useEffect(() => {
        if (peekTimerRef.current) {
            clearTimeout(peekTimerRef.current)
            peekTimerRef.current = null
        }
        if (sheetOpen) {
            setPeekText(null)
            return
        }
        // Élève absent : l'aperçu s'ouvrirait et se refermerait pendant qu'il
        // regarde ailleurs, donc il ne verrait jamais le message.
        if (!isStudentPresent) return
        if (lastMessage?.role !== "sam") return
        const content = lastMessage.content
        if (!content) return
        setPeekText(content)
        peekTimerRef.current = setTimeout(() => setPeekText(null), 7000)
        return () => {
            if (peekTimerRef.current) {
                clearTimeout(peekTimerRef.current)
                peekTimerRef.current = null
            }
        }
    }, [sheetOpen, lastMessage?.id, isStudentPresent])

    // D-2 proactif — coupé dès que la copie est rendue : la fin de
    // l'activité ne parle plus de Sam.
    useSamProactivity({
        activityId,
        enabled:
            wrapperReady &&
            !submitted &&
            // Une relance « tu avances bien ? » n'a de sens que devant
            // quelqu'un. Les compteurs repartent à son retour.
            isStudentPresent,
        sheetOpen,
    })

    // Push the activity into Redux so ActivityWrapperScreen renders it.
    // The wrapper clears `currentActivity` on its own unmount; we also
    // clear it on Sam unmount as a defensive belt against
    // strict-mode / route-replace races where the embedded wrapper
    // tears down after the next Sam screen has already mounted.
    const storeActivityIdRef = useRef(wrapperActivityId)
    useEffect(() => {
        storeActivityIdRef.current = wrapperActivityId
    }, [wrapperActivityId])
    useEffect(() => {
        if (!todoActivity) return
        dispatch(
            setCurrentActivityAction({
                activityData: todoActivity,
                progressStatus: "ongoing",
            })
        )
        return () => {
            // When we've degraded to the standard activity flow,
            // `launchActivity` already re-set `currentActivity` for the
            // regular `activity` screen — don't null it back out on Sam's
            // unmount or the fallback screen loses its activity.
            if (degradedRef.current) return
            // Seulement si c'est encore la nôtre. « Activité suivante avec
            // Sam » monte l'écran suivant avant que celui-ci parte (transition
            // du `replace`) : il a déjà posé SON activité, et la vider ici le
            // renvoyait à l'accueil, comme une sortie.
            if (storeActivityIdRef.current !== todoActivity.id) return
            dispatch(setCurrentActivityAction(null))
        }
    }, [dispatch, todoActivity?.id])

    // La seule sortie de la séance, quelle qu'en soit la raison (« Quitter »,
    // retour à la todo, activité introuvable, contexte en échec sans activité
    // à relancer). Elle se marque d'abord, pour que les filets ci-dessous ne
    // repartent pas une seconde fois.
    //
    // Sans historique — séance ouverte par URL, rechargement, lien partagé —
    // `goBack()` ne part pas, et l'écran restait sur Sam sans activité à
    // rendre, en chargement pour toujours. L'écran est nommé explicitement :
    // sur web, l'URL `/menu` seule rend « Page introuvable », c'est
    // `/menu/home` qui porte la liste d'activités.
    const leaveSession = useCallback(() => {
        degradedRef.current = true
        dispatch(setCurrentActivityAction(null))
        if (navigation.canGoBack()) navigation.goBack()
        else navigation.replace("menu", { screen: "home" })
    }, [dispatch, navigation])

    // Filet : si l'activité courante disparaît sous le wrapper sans passer
    // par sa sortie (« Quitter » appelle `onExit`, donc `leaveSession`), Sam
    // ne doit pas rester sur sa roue. On le lit sur le fait OBSERVABLE : le
    // wrapper était prêt, il ne l'est plus.
    useEffect(() => {
        if (wrapperReady) wrapperWasReadyRef.current = true
    }, [wrapperReady])

    useEffect(() => {
        if (
            !wrapperWasReadyRef.current ||
            wrapperReady ||
            submitted ||
            completion ||
            degradedRef.current
        ) {
            return
        }
        leaveSession()
    }, [wrapperReady, submitted, completion, leaveSession])

    // L'écran de fin d'une activité menée avec Sam est le nôtre : le wrapper
    // nous passe la charge du congrats (`setCompletion`) au lieu de pousser le
    // standard par-dessus Sam — tout de suite pour une copie sans correction,
    // à la sortie de la correction sinon.

    // The wrapper flips progressStatus to "finished" once the activity
    // is validated. La feuille se ferme : ce qui suit, c'est l'attente et
    // la correction de l'écran d'activité, comme hors session Sam.
    useEffect(() => {
        if (
            wrapperReady &&
            wrapperProgressStatus === "finished" &&
            !submitted
        ) {
            setSubmitted(true)
            setSheetOpen(false)
        }
    }, [wrapperReady, wrapperProgressStatus, submitted])

    // Degrade path — if the Sam context fetch failed (404/500), don't hang
    // on an infinite spinner. Fall back to the standard activity flow so
    // the activity stays playable without the Sam chrome. When we still
    // have the cached todo activity, launch the regular activity screen
    // (replace so Sam is off the stack); otherwise bounce back to the todo.
    useEffect(() => {
        if (!hasError || degradedRef.current) return
        degradedRef.current = true
        if (todoActivity) {
            launchActivity(todoActivity, "replace")
        } else {
            leaveSession()
        }
    }, [hasError, todoActivity, launchActivity, leaveSession])

    // Second filet, distinct du précédent : le contexte peut répondre 200
    // alors que la todo en cache ne porte pas l'activité — entrée directe par
    // URL, rechargement, cache vidé, ou activité sortie de la todo entre-temps
    // (l'ouvrir la fait passer de `todo` à `not_reviewed`, donc disparaître de
    // `todo_activities`). Le garde ci-dessus ne couvre que l'ÉCHEC du
    // contexte ; ce cas-ci, lui, laissait tourner la roue sans une seule
    // erreur. On laisse à la todo le temps d'arriver, puis on rend la main.
    useEffect(() => {
        // `submitted` dans la garde : après validation l'activité quitte la
        // todo, et ce filet — prévu pour l'activité qui n'arrive JAMAIS —
        // renvoyait l'élève à sa liste au milieu de sa correction.
        if (!context || todoActivity || submitted || degradedRef.current) return
        // Et seulement quand la todo est ARRIVÉE. Ce filet vise l'activité
        // absente de la liste ; armé avant que la liste soit chargée, il
        // renvoyait l'élève à son accueil au bout de 6 s alors que tout allait
        // bien — il suffisait que la réponse du serveur dépasse ce délai
        // (connexion faible, ou serveur de dev mono-thread saturé).
        if (!todoLoaded) return
        const t = setTimeout(() => {
            if (todoActivity || degradedRef.current) return
            leaveSession()
        }, MISSING_TODO_GRACE_MS)
        return () => clearTimeout(t)
    }, [context, todoActivity, todoLoaded, submitted, leaveSession])

    useEffect(() => {
        if (!context || trackedStartRef.current) return
        trackedStartRef.current = true
        amplitude.track("sam.session.started", {
            activity_id: activityId,
            entry_point: route.params?.entry_point || "todo",
            source_kind: context.source_kind,
        })
    }, [context, activityId, route.params?.entry_point])

    // Fire `sam.session.abandoned` when the screen unmounts before the
    // student validates the activity. The completion path tracks its
    // own event so we skip it here.
    //
    // Le compte de messages passe par une ref : l'effet de démontage a un
    // tableau de dépendances vide, il fige donc ce qu'il capture à la
    // PREMIÈRE frame — soit zéro. L'événement qui mesure le non-engagement
    // remontait ainsi invariablement « 0 message », alors que la métrique de
    // succès du lot est justement le taux de sessions avec au moins un
    // message élève.
    const messagesCountRef = useRef(0)
    useEffect(() => {
        messagesCountRef.current = conversation.messages.length
    }, [conversation.messages.length])

    useEffect(() => {
        return () => {
            if (trackedCompletionRef.current) return
            amplitude.track("sam.session.abandoned", {
                activity_id: activityId,
                messages_count: messagesCountRef.current,
                time_spent_sec: Math.round(
                    (Date.now() - startedAtRef.current) / 1000
                ),
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Compté à la validation de la copie : l'élève qui quitte pendant
    // l'attente de sa correction a bien terminé sa séance.
    useEffect(() => {
        if (!submitted || trackedCompletionRef.current) return
        trackedCompletionRef.current = true
        amplitude.track("sam.session.completed", {
            activity_id: activityId,
            messages_count: conversation.messages.length,
            duration_sec: Math.round(
                (Date.now() - startedAtRef.current) / 1000
            ),
        })
    }, [submitted, activityId, conversation.messages.length])

    // Deux libellés par action : court pour la pastille d'une conversation en
    // cours, à la première personne pour la carte de démarrage. C'est la même
    // action — la feuille choisit sa présentation selon le moment.
    const quickReplies = useMemo(() => {
        return [
            {
                key: "explain",
                icon: "file-alt",
                label: "Explique l'énoncé",
                starterLabel: "Je ne comprends pas l'énoncé",
                // Entrée d'OUVERTURE seulement : elle sert à démarrer quand on
                // ne sait pas quoi écrire. Une fois la conversation lancée,
                // l'élève reformule de lui-même — et la garder aurait poussé
                // « Rappel de cours » hors de l'écran sur un mobile de 390 px.
                starterOnly: true,
                onPress: () =>
                    conversation.sendText(
                        "Je ne comprends pas l'énoncé, tu peux me l'expliquer ?"
                    ),
            },
            {
                key: "hint",
                icon: "lightbulb",
                label: "Indice",
                starterLabel: "Donne-moi un indice pour démarrer",
                onPress: conversation.askHint,
            },
            {
                key: "course-reminder",
                icon: "book-open",
                label: "Rappel de cours",
                starterLabel: "Rappelle-moi le cours",
                onPress: conversation.askCourseReminder,
            },
        ]
    }, [
        conversation.askHint,
        conversation.askCourseReminder,
        conversation.sendText,
    ])

    // D-1 photo intermédiaire — première pastille, pleine, dans la MÊME
    // rangée que les intentions conversationnelles. Elle occupait avant un
    // bouton pleine largeur avec un sous-titre, soit une deuxième rangée de
    // commandes au-dessus du composeur.
    //
    // Tout le sens tient donc dans le libellé + l'icône appareil photo :
    // « Où j'en suis » ne peut pas se confondre avec rendre son travail — ce
    // que « scanner mon travail », le bouton de l'activité juste derrière,
    // fait vraiment. Le libellé est aussi COURT à dessein : mesuré sur un
    // iPhone de 390 px, « Regarde mon avancement » poussait « Rappel de
    // cours » hors de l'écran, ce qui déplaçait le défaut de visibilité au
    // lieu de le corriger. La caméra réutilise le même flux que la soumission
    // standard (CameraComponent), donc web et natif à l'identique.
    const primaryAction = useMemo(() => {
        if (submitted) return null
        return {
            key: "sam-work-check",
            icon: "camera",
            label: workCheck.isUploading ? "Envoi…" : "Où j'en suis",
            starterLabel: "Regarde où j'en suis en photo",
            onPress: workCheck.pickFromCamera,
            disabled: workCheck.isUploading,
        }
    }, [submitted, workCheck.isUploading, workCheck.pickFromCamera])

    // L'écran n'est jouable qu'avec le contexte Sam ET l'activité complète.
    // Au-delà d'un court délai, on DIT ce qu'on attend : une roue muette
    // laisse l'élève juger que « ça ne marche pas », et il n'a pas tort de le
    // croire — l'attente peut venir du réseau comme du serveur.
    const waiting = isLoading || !context || !todoActivity
    const [waitHint, setWaitHint] = useState(false)
    useEffect(() => {
        if (!waiting) {
            setWaitHint(false)
            return
        }
        const t = setTimeout(() => setWaitHint(true), 1500)
        return () => clearTimeout(t)
    }, [waiting])

    // L'attente qui a un objet et des étapes se raconte ; les trois points
    // d'un chat sont calibrés pour deux secondes. Ici l'analyse d'une photo
    // enchaîne un téléversement S3 et une passe de vision : plusieurs
    // secondes de silence, d'où l'impression de bug.
    const analysis = useMemo(
        () =>
            workCheck.isUploading
                ? { active: true, label: "Je regarde ton travail…" }
                : null,
        [workCheck.isUploading]
    )

    if (waiting) {
        // Une roue muette laisse l'élève juger que « ça ne marche pas ». On ne
        // met le texte qu'au bout d'un court délai : sur un chargement rapide,
        // il n'aurait fait que clignoter.
        return (
            <LoadingScreen
                message={
                    waitHint ? "On prépare ta séance avec Sam…" : undefined
                }
            />
        )
    }

    if (completion) {
        return (
            <MissionCompletionView
                congratsData={completion.congratsData}
                nextActivityId={nextActivityId}
                onBackToTodo={() => {
                    conversation.reset()
                    leaveSession()
                }}
                onNextActivity={(nextActivityId) => {
                    conversation.reset()
                    navigation.replace("sam-session", { activityId: nextActivityId })
                }}
            />
        )
    }

    // La feuille de Sam se SUPERPOSE à l'activité, elle ne la pousse pas.
    // Lui donner une largeur en pixels (viewport − 420) décalait le contenu
    // hors du cadre : le wrapper centre sur la largeur de la FENÊTRE, pas sur
    // celle de son conteneur, si bien que l'énoncé se retrouvait coupé à
    // gauche. Le panneau est déjà rendu dans un calque absolu au-dessus —
    // l'activité garde donc toute sa largeur.

    return (
        <View style={styles.root}>
            <View style={[styles.activityContainer, { width: "100%" }]}>
                {wrapperReady ? (
                    // La tuile de Sam flotte au-dessus de l'activité : son contenu lui laisse la
                    // place de remonter au-dessus d'elle en fin de défilement.
                    <SamTileClearance.Provider value={SAM_TILE_CLEARANCE}>
                        <ActivityWrapperScreen
                            key={activityId}
                            navigation={navigation}
                            route={route}
                            onExit={leaveSession}
                            onCompletionScreen={setCompletion}
                        />
                    </SamTileClearance.Provider>
                ) : (
                    <LoadingScreen />
                )}
            </View>

            {!sheetOpen && !submitted && (
                <SamFloatingTile
                    onPress={openSheet}
                    samMood={conversation.lastMood}
                    samNudgeKey={conversation.messages.length}
                    hasUnread={lastMessage?.role === "sam"}
                    peekText={peekText}
                    // L'aperçu se ferme aussi à la main : il tient 7 s, ce qui
                    // est long quand on lit un énoncé juste derrière.
                    onDismissPeek={() => setPeekText(null)}
                />
            )}

            {sheetOpen && (
                <Animated.View
                    entering={FadeIn.duration(220)}
                    exiting={FadeOut.duration(180)}
                    style={StyleSheet.absoluteFillObject}
                    pointerEvents="box-none"
                >
                    <TouchableOpacity
                        activeOpacity={1}
                        onPress={closeSheet}
                        style={[
                            StyleSheet.absoluteFillObject,
                            styles.backdrop,
                        ]}
                    />
                </Animated.View>
            )}

            <SamConversationSheet
                visible={sheetOpen}
                onClose={closeSheet}
                samMood={conversation.lastMood}
                samNudgeKey={conversation.messages.length}
                // Sam « réfléchit » aussi pendant une analyse de photo : le
                // statut, le halo et la mise en veille des actions valent dans
                // les deux cas. Ce qu'on RACONTE de l'attente, en revanche,
                // passe par `analysis` — les trois points ne suffisent pas
                // pour plusieurs secondes.
                isSamThinking={conversation.isThinking || workCheck.isUploading}
                analysis={analysis}
                chatHistory={conversation.messages}
                onSendText={conversation.sendText}
                quickReplies={quickReplies}
                primaryAction={primaryAction}
                capReached={conversation.capReached}
                capScope={conversation.capScope}
                degraded={conversation.degraded}
            />
        </View>
    )
}

const styles = StyleSheet.create({
    root: {
        flex: 1,
        backgroundColor: theme.colors.white,
    },
    activityContainer: {
        flex: 1,
        backgroundColor: theme.colors.white,
    },
    backdrop: {
        // Un voile CLAIR, pas un rideau sombre. L'énoncé est noir sur blanc :
        // un scrim bleu nuit à 45 % l'effaçait, alors que toute la
        // fonctionnalité consiste à aider sur cet exercice-là. Ici le texte
        // passe en gris pâle — présent, relisable, visiblement au second plan.
        // C'est ce que font Best Buy, Fabric et Craft ; le rideau sombre n'est
        // de mise que sur un fond déjà sombre (Spotify).
        // 0,32 et pas davantage : à 0,62 le voile clair effaçait l'énoncé au
        // lieu de le reculer, et tout l'écran paraissait délavé. Mesuré à
        // l'écran, c'est le point où le texte reste lisible tout en passant
        // visiblement au second plan.
        backgroundColor: "rgba(240, 244, 246, 0.32)",
    },
})

export default SamSessionScreen
